import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { Broadcast, CircleNotch, Check, CaretDown, Plus, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const AddToMonitoringBtn = ({ npi, providerName, specialty, state }) => {
  const { user, isAuthenticated, loading: authLoading, login, authFetch } = useAuth();
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addedTo, setAddedTo] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creating, setCreating] = useState(false);

  const loadLists = useCallback(async () => {
    if (!user) return;
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists`);
      if (res.ok) { const data = await res.json(); setLists(data.lists || []); }
    } catch { /* ignore */ }
  }, [user, authFetch]);

  useEffect(() => { if (open) loadLists(); }, [open, loadLists]);

  if (authLoading) return null;

  if (!isAuthenticated) {
    return (
      <Button onClick={() => login({ appState: { returnTo: window.location.pathname } })}
        variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" data-testid="monitor-login-btn">
        <Broadcast className="w-4 h-4" /> Monitor
      </Button>
    );
  }

  if (!user) {
    return (
      <Button variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" disabled>
        <CircleNotch className="w-4 h-4 animate-spin" /> Monitor
      </Button>
    );
  }

  const tier = user?.tier || "free";
  if (tier === "free") return null;

  const handleAdd = async (listId) => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ npi, provider_name: providerName, specialty, state }),
      });
      if (res.ok) {
        setAddedTo(listId);
        setTimeout(() => { setAddedTo(null); setOpen(false); }, 1200);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const handleCreateAndAdd = async () => {
    if (!newListName.trim()) return;
    setCreating(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newListName.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        // Add provider to the newly created list
        await handleAdd(data.list_id);
        setNewListName("");
        setShowCreate(false);
        loadLists();
      }
    } catch { /* ignore */ } finally { setCreating(false); }
  };

  return (
    <div className="relative">
      <Button onClick={() => setOpen(!open)}
        variant="outline" size="sm" className="gap-1.5 rounded-lg text-sm border-slate-300 hover:border-violet-400 hover:text-violet-600" data-testid="monitor-btn">
        <Broadcast className="w-4 h-4" /> Monitor <CaretDown className="w-3 h-3 opacity-50" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setShowCreate(false); }} />
          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl w-72 z-50" data-testid="monitor-dropdown">
            <div className="px-3 pt-3 pb-1 flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Add to monitoring list</p>
              <button onClick={() => { setOpen(false); setShowCreate(false); }} className="text-slate-300 hover:text-slate-500"><X className="w-3.5 h-3.5" /></button>
            </div>

            {/* Existing lists */}
            {lists.length > 0 && (
              <div className="py-1">
                {lists.map(lst => (
                  <button key={lst.list_id} onClick={() => handleAdd(lst.list_id)} disabled={loading}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{lst.name}</p>
                      <p className="text-xs text-slate-400">{lst.provider_count || 0} providers</p>
                    </div>
                    {addedTo === lst.list_id ? <Check className="w-4 h-4 text-emerald-500 shrink-0" weight="bold" />
                      : loading ? <CircleNotch className="w-4 h-4 text-slate-300 animate-spin shrink-0" /> : null}
                  </button>
                ))}
              </div>
            )}

            {lists.length === 0 && !showCreate && (
              <p className="px-3 py-3 text-sm text-slate-400">No lists yet — create one below.</p>
            )}

            {/* Create new list */}
            <div className="border-t border-slate-100 p-2">
              {showCreate ? (
                <div className="space-y-2">
                  <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
                    placeholder="List name (e.g. Texas Providers)" autoFocus
                    onKeyDown={e => e.key === "Enter" && handleCreateAndAdd()}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30" />
                  <div className="flex items-center gap-2">
                    <Button onClick={handleCreateAndAdd} disabled={creating || !newListName.trim()} size="sm"
                      className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-1.5 flex-1 text-xs">
                      {creating ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Create & Add
                    </Button>
                    <button onClick={() => { setShowCreate(false); setNewListName(""); }} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowCreate(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-teal hover:bg-teal/5 rounded-lg transition-colors">
                  <Plus className="w-4 h-4" /> Create new list
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddToMonitoringBtn;
